import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, FolderOpen, User, Building, ArrowRight, Clock, Headset, Workflow, Loader2 } from 'lucide-react';
import GlassPanel from '../ui/GlassPanel';
import ProjectDetail from '../ProjectDetail/ProjectDetail';
import { useAuth } from '../../useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import ProjectService from '../../services/project.service';
import OrganizationService from '../../services/organization.service';
import { messageForResult } from '../../services/accountDomainErrorMessages';
import AssistantService from '../../services/assistant.service';
import WorkflowService from '../../services/workflow.service';
import { getProjectBackground } from '../../assets/projects';
import FadeImg from '../ui/FadeImg';
import AvatarStack from '../ui/AvatarStack';

// Cuanto se espera a que ?projectId= resuelva antes de mostrar la lista.
const DEEP_LINK_TIMEOUT_MS = 5000;

/**
 * @param {object} [baseParams]  parámetros que la vista mantiene en la URL al
 *   navegar dentro de sí misma. En `/dashboard` es `{ view: 'Projects' }`, que
 *   es lo que selecciona esta vista; bajo `/a/:id/projects` la ruta ya la
 *   selecciona, así que ahí sobra — y dejarlo escrito ensuciaría la URL con un
 *   parámetro que no significa nada.
 * @param {(project: object) => void} [onOpenProject]  qué hacer al abrir un
 *   proyecto. Sin esto la vista abre el detalle dentro de sí misma, que es el
 *   comportamiento de siempre en `/dashboard`. Pasándolo, la vista queda en
 *   modo lista: no monta `ProjectDetail` ni resuelve deep-links de agente, y
 *   abrir un proyecto es cosa de quien la monta. Configuración lo usa para
 *   mandar el workspace a la app principal, que es donde vive.
 */
export default function ProjectsView({ baseParams = { view: 'Projects' }, onOpenProject = null }) {
  const { user, isAuthenticated } = useAuth();
  const { 
    projects, 
    projectsLoading: loading, 
    currentContext, 
    currentAccount,
    createProject: createProjectContext 
  } = useOrganization();
  
  const [searchParams, setSearchParamsRaw] = useSearchParams();
  // Todas las navegaciones internas conservan `baseParams`, para que la vista
  // funcione igual colgada de una ruta o de otra.
  const setSearchParams = useCallback(
    (next) => setSearchParamsRaw({ ...baseParams, ...next }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParamsRaw, JSON.stringify(baseParams)]
  );
  const urlProjectId = searchParams.get('projectId');
  const urlTab = searchParams.get('tab') || 'assistants';
  const urlAgentId = searchParams.get('agentId');
  // Quien pasa `onOpenProject` se lleva el detalle a otra parte; aquí solo se
  // lista. Todo lo que sigue —la resolución del proyecto de la URL, la del
  // agente, el propio `ProjectDetail`— existe para el detalle, así que en modo
  // lista no corre.
  const embedsDetail = !onOpenProject;
  
  const [authError, setAuthError] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createProjectError, setCreateProjectError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [orgMembers, setOrgMembers] = useState([]);
  const [projectStats, setProjectStats] = useState({}); // { [projectId]: { agents, workflows } }
  const [newProject, setNewProject] = useState({
    name: '',
    description: ''
  });
  const [fetchedProject, setFetchedProject] = useState(null);
  const [fetchingProject, setFetchingProject] = useState(false);

  // Encontrar proyecto seleccionado basado en URL
  const projectFromList = urlProjectId
    ? projects.find(p => p.project_id === urlProjectId)
    : null;
  const selectedProject = projectFromList || fetchedProject;
  const showProjectDetails = embedsDetail && !!selectedProject;

  // Recargar dentro de un proyecto (o de un agente) llegaba con ?projectId= en
  // la URL pero sin el proyecto todavia, asi que se pintaba la lista un
  // instante —y con `projects` vacia, el estado "No projects yet", que afirma
  // que no hay ninguno mientras se estan cargando. Mientras el proyecto de la
  // URL se resuelve no se pinta la lista: se espera.
  //
  // Se deja de esperar cuando ya se intento y no aparecio, para que un
  // projectId invalido caiga a la lista en vez de dejar un loader fijo.
  const projectLookupDone = !loading && !fetchingProject && fetchedProject === null;
  // Mismo tope que en ProjectDetail y por lo mismo: si la carga no llega a
  // arrancar, la condicion de arriba no se cumple nunca y el loader se queda.
  const [projectLookupTimedOut, setProjectLookupTimedOut] = useState(false);
  useEffect(() => {
    if (!urlProjectId || selectedProject || projectLookupTimedOut) return undefined;
    const timer = setTimeout(() => setProjectLookupTimedOut(true), DEEP_LINK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [urlProjectId, selectedProject, projectLookupTimedOut]);

  const resolvingProject =
    embedsDetail && Boolean(urlProjectId) && !selectedProject && !projectLookupDone && !projectLookupTimedOut;

  // Al cambiar de cuenta, limpiar proyecto seleccionado para evitar errores de proyecto inválido
  const prevAccountIdRef = useRef(currentAccount?.id);
  useEffect(() => {
    const prevId = prevAccountIdRef.current;
    prevAccountIdRef.current = currentAccount?.id;
    if (prevId && currentAccount?.id && prevId !== currentAccount?.id && urlProjectId) {
      setSearchParams({});
    }
  }, [currentAccount?.id]);

  // Si el proyecto no está en el state, llamar getProject directamente
  useEffect(() => {
    if (!embedsDetail) return;
    if (urlProjectId && !projectFromList && !fetchingProject) {
      setFetchingProject(true);
      setFetchedProject(null);
      ProjectService.getProject(urlProjectId).then(result => {
        if (result.success) setFetchedProject(result.data);
        setFetchingProject(false);
      });
    }
    if (!urlProjectId) {
      setFetchedProject(null);
    }
  }, [urlProjectId, projectFromList, embedsDetail]);

  // Deep-link to an agent: when ?agentId= arrives without a projectId (e.g. from
  // a Zeno card), resolve which project the agent belongs to and rewrite the URL
  // so its ProjectDetail opens. The agentId stays in the URL so ProjectDetail can
  // auto-open the agent.
  const agentResolveRef = useRef(false);
  useEffect(() => {
    if (!embedsDetail || !urlAgentId || urlProjectId || agentResolveRef.current) return;
    agentResolveRef.current = true;
    AssistantService.getAssistant(urlAgentId).then((result) => {
      const pid = result?.success ? (result.data?.projectId || result.data?.project_id) : null;
      if (pid) {
        setSearchParams({ projectId: pid, tab: 'assistants', agentId: urlAgentId });
      } else {
        // Couldn't resolve (deleted / not accessible) — drop agentId so we don't loop.
        setSearchParams({});
      }
    }).catch(() => {
      setSearchParams({});
    });
  }, [urlAgentId, urlProjectId, setSearchParams, embedsDetail]);

  // Obtener lista de proyectos - REMOVED: Now using context
  // const fetchProjects = async () => { ... } // Using useOrganization context instead

  // Crear nuevo proyecto
  const createProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;

    try {
      const projectData = {
        name: newProject.name,
        description: newProject.description
      };

      // No necesitamos agregar account_id o organization_id manualmente
      // El contexto se encarga de eso
      
      const result = await createProjectContext(projectData);

      if (result.success) {
        setCreateProjectError(null);
        setShowCreateForm(false);
        setNewProject({ name: '', description: '' });
        // No need to call fetchProjects - context handles this automatically
      } else {
        console.error('Error creating project:', result.error);
        setCreateProjectError(messageForResult(result, 'Failed to create project'));
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setCreateProjectError(error.message);
    }
  };

  // useEffect removed - using OrganizationContext instead of manual fetching

  // Manejar click en proyecto
  const handleProjectClick = async (project) => {
    if (onOpenProject) {
      onOpenProject(project);
      return;
    }
    // Actualizar URL con el projectId
    setSearchParams({ projectId: project.project_id, tab: 'assistants' });
  };

  // Manejar regreso desde vista de detalles
  const handleBackToProjects = () => {
    // Limpiar projectId de la URL
    setSearchParams({});
  };

  // Manejar cambio de tab en ProjectDetail
  const handleTabChange = (tab) => {
    setSearchParams({ projectId: urlProjectId, tab });
  };

  const isOrgContext = currentContext?.type === 'ORGANIZATION';

  // Cargar miembros de la organización para mostrar las burbujas en las tarjetas.
  // En contexto personal solo se muestra el usuario actual (no hay llamada al backend).
  useEffect(() => {
    const orgId = currentAccount?.organization_id || (isOrgContext ? currentContext?.id : null);
    if (isOrgContext && orgId) {
      let cancelled = false;
      OrganizationService.listMembers(orgId).then((result) => {
        if (!cancelled && result?.success && Array.isArray(result.data)) {
          setOrgMembers(result.data);
        }
      });
      return () => { cancelled = true; };
    }
    setOrgMembers([]);
  }, [isOrgContext, currentAccount?.organization_id, currentContext?.id]);

  // Conteos reales por proyecto (agents + workflows) para los pills de cada tarjeta.
  // Agents: una sola llamada por cuenta agrupada por projectId.
  // Workflows: el endpoint exige project_id, así que se pide por proyecto en paralelo.
  const accountId = currentAccount?.id;
  const projectIdsKey = projects.map((p) => p.project_id).join(',');
  useEffect(() => {
    if (!accountId || projects.length === 0) return;
    let cancelled = false;

    AssistantService.listAssistants({ accountId, limit: 100 }).then((res) => {
      if (cancelled || !res?.success) return;
      const byProject = {};
      (res.data || []).forEach((a) => {
        if (a.projectId) byProject[a.projectId] = (byProject[a.projectId] || 0) + 1;
      });
      setProjectStats((prev) => {
        const next = { ...prev };
        projects.forEach((p) => {
          next[p.project_id] = { ...(next[p.project_id] || {}), agents: byProject[p.project_id] || 0 };
        });
        return next;
      });
    }).catch(() => {});

    projects.forEach((p) => {
      WorkflowService.listWorkflows({
        account_id: accountId,
        project_id: p.project_id,
        limit: 100,
        include_definition: false,
      }).then((res) => {
        if (cancelled || !res?.success) return;
        const count = (res.data?.workflows || []).length;
        setProjectStats((prev) => ({
          ...prev,
          [p.project_id]: { ...(prev[p.project_id] || {}), workflows: count },
        }));
      }).catch(() => {});
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, projectIdsKey]);

  // Miembros a mostrar en cada tarjeta: la org completa, o solo el usuario en personal.
  const cardMembers = useMemo(() => {
    if (isOrgContext) return orgMembers;
    if (user) {
      return [{
        user_id: user.id || user.user_id || user.email,
        display_name: user.username || user.name || user.email,
        email: user.email,
        avatar_url: user.avatar_url || user.avatarUrl,
      }];
    }
    return [];
  }, [isOrgContext, orgMembers, user]);

  // Filtrar proyectos por búsqueda y por el filtro activo
  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!matchesSearch) return false;
    if (activeFilter === 'active') return (project.status || 'active').toLowerCase() === 'active';
    return true;
  });

  // Sin "Personal" ni "Organizational": la lista es la de **una** cuenta, y una
  // cuenta es de una de las dos cosas. Filtrar por eso solo podía devolver todo
  // o nada, y una opción que no puede cambiar lo que ves es peor que no tenerla.
  const filterChips = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
  ];

  // Función helper para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Fecha relativa tipo "2h ago" para el pie de la tarjeta
  const formatRelative = (dateString) => {
    if (!dateString) return null;
    const then = new Date(dateString).getTime();
    if (Number.isNaN(then)) return null;
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(dateString);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Si estamos en vista de detalles, mostrar ProjectDetail */}
      {resolvingProject ? (
        <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-sm text-white/50">
          <Loader2 className="h-4 w-4 animate-spin" />
          Opening project…
        </div>
      ) : showProjectDetails && selectedProject ? (
        <div className="h-full opacity-0 animate-scale-reveal [animation-fill-mode:forwards]">
          <ProjectDetail 
            project={selectedProject} 
            onBack={handleBackToProjects}
            activeTab={urlTab}
            onTabChange={handleTabChange}
          />
        </div>
      ) : (
        // The route wraps Projects in h-screen + overflow-hidden, so something
        // here has to own the scroll. This used to sit on the grid alone via a
        // `flex-1 overflow-y-auto` whose `flex-1` was inert (parent wasn't a
        // flex box), leaving the page unscrollable. Scroll the whole column
        // instead — on a phone the header/search/stats already fill the screen.
        <div className="min-h-0 flex-1 overflow-y-auto pb-6 pr-1 opacity-0 animate-blur-reveal [animation-fill-mode:forwards]">
          {/* Header */}
          <div className="mb-6">
            <div className="min-w-0 opacity-0 animate-reveal-left" style={{ animationDelay: '0.1s' }}>
              <h1 className="text-2xl font-bold text-white mb-1">Projects</h1>
              <p className="text-white/70 text-sm">
                Manage your projects for {currentContext?.type === 'ORGANIZATION' ? currentContext.name : 'personal account'}
              </p>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6 opacity-0 animate-expand-in" style={{ animationDelay: '0.15s' }}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 text-sm"
              />
            </div>
            <div className="flex items-start gap-2">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                {filterChips.map((chip) => (
                  <button
                    key={chip.id}
                    onClick={() => setActiveFilter(chip.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 border sm:px-4 sm:py-2 sm:text-sm ${
                      activeFilter === chip.id
                        ? 'bg-primary border-[#7c3aed] text-white shadow-lg shadow-purple-500/25'
                        : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:border-white/20'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Sits at the end of the controls row, like Create Avatar does.
                  Icon-only on phones; the label returns from sm up. */}
              <button
                onClick={() => setShowCreateForm(true)}
                aria-label="Create New Project"
                title="Create New Project"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary text-white transition-colors hover:bg-violet-700 text-sm font-medium sm:h-10 sm:w-auto sm:px-4"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Create New Project</span>
              </button>
            </div>
          </div>


          {/* Projects Grid */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {authError ? (
                <div className="col-span-full text-center py-12">
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 max-w-md mx-auto">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-red-400 mb-2">Authentication Error</h3>
                    <p className="text-red-300 text-sm mb-4">
                      Your session has expired or the authentication token is invalid. Please sign in again.
                    </p>
                    <button
                      onClick={() => {
                        localStorage.clear();
                        window.location.href = '/login';
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl font-medium transition text-sm"
                    >
                      Sign In Again
                    </button>
                  </div>
                </div>
              ) : loading ? (
                /* Skeleton loading cards */
                Array.from({ length: 6 }).map((_, i) => (
                  <div 
                    key={i} 
                    className="p-4 bg-white/5 border border-gray-800/50 rounded-xl opacity-0 animate-reveal-up [animation-fill-mode:forwards]"
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-6 w-6 bg-white/10 rounded-lg skeleton" />
                      <div className="h-5 w-16 bg-white/10 rounded-full skeleton" />
                    </div>
                    <div className="h-5 w-3/4 bg-white/10 rounded mb-2 skeleton" />
                    <div className="h-4 w-full bg-white/10 rounded mb-1 skeleton" />
                    <div className="h-4 w-2/3 bg-white/10 rounded mb-3 skeleton" />
                    <div className="space-y-1">
                      <div className="h-3 w-1/2 bg-white/10 rounded skeleton" />
                      <div className="h-3 w-1/3 bg-white/10 rounded skeleton" />
                    </div>
                  </div>
                ))
              ) : filteredProjects.length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <FolderOpen className="h-12 w-12 text-white/30 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white/80 mb-1">
                    {searchTerm ? 'No projects found' : 'No projects yet'}
                  </h3>
                  <p className="text-white/60 mb-4 text-sm">
                    {searchTerm 
                      ? 'Try different search terms' 
                      : 'Create your first project to get started'
                    }
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="bg-primary hover:bg-violet-700 text-white px-4 py-2 rounded-xl font-medium transition text-sm"
                    >
                      Create First Project
                    </button>
                  )}
                </div>
              ) : (
                filteredProjects.map((project, index) => {
                  const cover = getProjectBackground(index);
                  const relative = formatRelative(project.updated_at || project.created_at);
                  const stats = projectStats[project.project_id] || {};
                  return (
                    <div
                      key={project.project_id}
                      className="relative flex flex-col min-h-[340px] border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer hover:border-purple-500/40 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/10 opacity-0 animate-slide-blur group"
                      style={{ animationDelay: `${index * 0.06}s` }}
                      onClick={() => handleProjectClick(project)}
                    >
                      {/* Full-card background image */}
                      <FadeImg
                        src={cover}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105"
                      />
                      {/* Readability overlay: image visible on top, darkens toward content */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/20" />

                      {/* Floating content */}
                      <div className="relative z-10 flex flex-col h-full flex-1 p-4">
                        {/* Top badges */}
                        <div className="flex items-start justify-between">
                          <span className="flex items-center gap-1 text-[10px] font-medium text-white/90 bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/10">
                            {project.organization_id ? <Building className="h-2.5 w-2.5" /> : <User className="h-2.5 w-2.5" />}
                            {project.organization_id ? 'Organizational' : 'Personal'}
                          </span>
                          <span className="text-[10px] font-medium text-white bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/10 capitalize">
                            {project.status || 'active'}
                          </span>
                        </div>

                        {/* Bottom block */}
                        <div className="mt-auto">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-base font-semibold text-white line-clamp-1 drop-shadow">
                              {project.name}
                            </h3>
                            <AvatarStack items={cardMembers} max={3} />
                          </div>
                          <p className="text-white/60 text-xs mb-3 line-clamp-2 min-h-[2rem]">
                            {project.description || 'No description'}
                          </p>

                          {/* Stat pills */}
                          <div className="flex flex-wrap items-center gap-1.5 mb-3">
                            <span className="flex items-center gap-1 text-[11px] font-medium text-white/80 bg-white/10 border border-white/10 px-2 py-0.5 rounded-md backdrop-blur-sm">
                              <Headset className="h-3 w-3 text-purple-300" />
                              {stats.agents ?? 0} Agents
                            </span>
                            <span className="flex items-center gap-1 text-[11px] font-medium text-white/80 bg-white/10 border border-white/10 px-2 py-0.5 rounded-md backdrop-blur-sm">
                              <Workflow className="h-3 w-3 text-purple-300" />
                              {stats.workflows ?? 0} Workflows
                            </span>
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-2.5 border-t border-white/10">
                            <div className="flex items-center gap-1.5 text-[11px] text-white/50">
                              <Clock className="h-3 w-3" />
                              {relative ? `Updated ${relative}` : `Created ${formatDate(project.created_at)}`}
                            </div>
                            <div className="flex items-center gap-1 text-xs font-medium text-purple-300 transition-colors group-hover:text-purple-200">
                              Open project
                              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && !showProjectDetails && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowCreateForm(false)}
          />

          <div className="relative bg-[#0a0a0a] border border-white/20 rounded-2xl w-full max-w-md p-6 text-white backdrop-blur-xl">
            <h2 className="text-xl font-bold text-white mb-4">Create New Project</h2>
            <form onSubmit={createProject} className="space-y-4">
              <div>
                <label className="block text-white mb-2 text-sm">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40"
                  placeholder="e.g., My Web Project"
                  maxLength={128}
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">
                  Description (optional)
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40 resize-none"
                  placeholder="Describe your project..."
                  rows={3}
                  maxLength={2000}
                />
              </div>
              {createProjectError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 text-sm">
                  <span className="mt-0.5">⚠️</span>
                  <span>{createProjectError}</span>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreateForm(false); setCreateProjectError(null); }}
                  className="flex-1 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition text-center"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-primary hover:bg-violet-700 text-white rounded-lg transition text-center"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}